import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

interface PipelineFunnelChartProps {
  stages: Array<{
    stage: string
    count: number
    total_value: number
    weighted_value: number
    avg_probability: number
  }>
}

export default function PipelineFunnelChart({ stages }: PipelineFunnelChartProps) {
  const data = {
    labels: stages.map(s => s.stage),
    datasets: [
      {
        label: 'Total Pipeline Value',
        data: stages.map(s => s.total_value),
        backgroundColor: 'rgba(47, 87, 123, 0.6)',
        borderColor: '#2f577b',
        borderWidth: 2,
      },
      {
        label: 'Weighted Value (Probability-Adjusted)',
        data: stages.map(s => s.weighted_value),
        backgroundColor: 'rgba(182, 138, 47, 0.6)',
        borderColor: '#b68a2f',
        borderWidth: 2,
      },
    ],
  }

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 12,
          },
          usePointStyle: true,
          padding: 15,
        },
      },
      title: {
        display: true,
        text: 'Pipeline Funnel by Stage',
        font: {
          family: "'Product Sans', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          size: 18,
          weight: 'bold' as const,
        },
        color: '#0b0b0b',
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(11, 11, 11, 0.9)',
        titleFont: {
          family: "'Product Sans', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          size: 14,
        },
        bodyFont: {
          family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          size: 13,
        },
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || ''
            if (label) {
              label += ': '
            }
            const value = context.parsed.x
            label += '$' + value.toLocaleString()
            
            // Add deal count and probability
            const stageIndex = context.dataIndex
            const stage = stages[stageIndex]
            if (stage) {
              label += ` (${stage.count} deals, ${Math.round(stage.avg_probability)}% avg probability)`
            }
            
            return label
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          font: {
            family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 11,
          },
          callback: function (value: any) {
            return '$' + (value / 1000).toLocaleString() + 'K'
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      y: {
        ticks: {
          font: {
            family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 12,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  }

  return (
    <div className="w-full h-[400px]">
      <Bar options={options} data={data} />
    </div>
  )
}
